import type { Part } from './chatReducer';

export type CrewStage =
  | 'case'
  | 'ems'
  | 'ca'
  | 'checks'
  | 'compliance'
  | 'pack'
  // The rate-sheet import reuses the steps strip (importSteps.ts).
  | 'read'
  | 'extract'
  | 'check'
  | 'compare';
export type StepTone =
  | 'wait'
  | 'active'
  | 'done'
  | 'revise'
  | 'failed'
  | 'stopped'
  | 'skipped';
export type CrewStep = {
  stage: CrewStage;
  title: string;
  tone: StepTone;
  description: string;
};

const ORDER: { stage: CrewStage; title: string }[] = [
  { stage: 'case', title: 'Case' },
  { stage: 'ems', title: 'EMS' },
  { stage: 'ca', title: 'CA' },
  { stage: 'checks', title: 'Checks' },
  { stage: 'compliance', title: 'Compliance' },
  { stage: 'pack', title: 'Pack' },
];

const TONES: Record<string, StepTone> = {
  started: 'active',
  done: 'done',
  revise: 'revise',
  failed: 'failed',
  skipped: 'skipped',
};

const WORDS: Record<StepTone, string> = {
  wait: '',
  active: 'Working…',
  done: 'Done',
  revise: 'Revise',
  failed: 'Failed',
  stopped: 'Stopped',
  skipped: 'Skipped',
};

/**
 * The steps strip (spec §10): Case -> EMS -> CA -> checks -> Compliance (round
 * n/2) -> Pack. Each stage shows its latest crew_progress event (Plan 4a: case
 * is round 0, the others rounds 1-2), so a reconnect or round 2 just moves it
 * on. A stage still `started` once the turn is no longer live was cut off
 * (a limit or an error), so it reads "Stopped". Null when the turn has no crew progress, i.e. it isn't a crew run.
 */
export function buildCrewSteps(
  parts: Part[],
  live: boolean,
): CrewStep[] | null {
  const latest = new Map<
    string,
    { status: string; round: number; detail: string | null }
  >();
  for (const part of parts) {
    if (part.kind === 'crew_progress') latest.set(part.stage, part);
  }
  if (latest.size === 0) return null;
  return ORDER.map(({ stage, title }): CrewStep => {
    const event = latest.get(stage);
    if (!event) return { stage, title, tone: 'wait', description: '' };
    // Own-property lookup: the status comes off the wire, so "constructor" must not resolve.
    let tone = Object.prototype.hasOwnProperty.call(TONES, event.status)
      ? TONES[event.status]
      : 'active';
    if (tone === 'active' && !live) tone = 'stopped';
    const word =
      stage === 'compliance' && tone === 'done' ? 'Pass' : WORDS[tone];
    const showRound = stage !== 'case' && stage !== 'pack' && event.round > 0;
    const round = showRound ? `round ${event.round}/2` : '';
    return {
      stage,
      title,
      tone,
      description: [word, round, event.detail ?? '']
        .filter(Boolean)
        .join(' · '),
    };
  });
}
